import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { clearSessionToken, setSessionToken } from "../lib/sessionToken";
import {
  showsPasswordToggle,
  usePasswordToggleVisibility,
} from "../lib/passwordFieldToggle";

export function useLoginAuth() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(false);
  const [pending2faToken, setPending2faToken] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  const passwordToggleVisible = usePasswordToggleVisibility(password);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!passwordToggleVisible) setShowPassword(false);
  }, [passwordToggleVisible]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("google") === "unregistered") {
      toast.error("No portal account for this Google email. Use a magic link or the password your studio shared.");
      const u = new URL(window.location.href);
      u.searchParams.delete("google");
      window.history.replaceState({}, "", `${u.pathname}${u.search}${u.hash}`);
    }
    if (p.get("google") === "invite_required") {
      toast.error(
        "Finish setup with your invite link first: open the email link once, then create a password or continue with Google."
      );
      const u = new URL(window.location.href);
      u.searchParams.delete("google");
      window.history.replaceState({}, "", `${u.pathname}${u.search}${u.hash}`);
    }
    if (p.get("google") === "retired") {
      toast.error(
        "Finish setup with your invite link first: open the email link once, then create a password or continue with Google."
      );
      const u = new URL(window.location.href);
      u.searchParams.delete("google");
      window.history.replaceState({}, "", `${u.pathname}${u.search}${u.hash}`);
    }
  }, []);

  const startGoogle = async () => {
    setGoogleBusy(true);
    try {
      const { data } = await api.get("/auth/google/login-url");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google sign-in is not available");
      setGoogleBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    setAccessDenied(false);
    try {
      const { data } = await api.post("/auth/request-magic-link", {
        email: email.trim().toLowerCase(),
      });
      setSent(true);
      if (data.email_delivered) {
        toast.success(data.message || "Check your inbox for the link.");
      } else if (data.magic_link) {
        try {
          await navigator.clipboard.writeText(data.magic_link);
          toast.info("Sign-in link copied to clipboard.", { duration: 6000 });
        } catch {
          toast.info(data.magic_link, { duration: 25000 });
        }
      } else {
        toast.success(data.message || "If registered, you'll receive a link shortly.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const d = typeof detail === "string" ? detail : "";
      if (err.response?.status === 403 && d === "DENIED") {
        setAccessDenied(true);
      } else {
        toast.error(d || "Could not send magic link.");
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * @param {{ commitSession?: boolean, boot?: boolean }} [opts]
   * - commitSession: false returns auth payload without setting session (for in-page ink).
   * - boot: when committing, whether to show the full-screen boot overlay (default true).
   */
  const passwordSignIn = async ({ commitSession = true, boot = true } = {}) => {
    const em = (email || "").trim().toLowerCase();
    const pw = (password || "").trim();
    if (!em) {
      toast.error("Enter your email");
      return null;
    }
    if (pw.length < 3) return null;
    setPwBusy(true);
    setAccessDenied(false);
    try {
      const { data } = await api.post("/auth/login", { email: em, password: pw });
      if (data.needs_onboarding) {
        navigate(`/onboarding?token=${encodeURIComponent(data.onboarding_token)}`, { replace: true });
        return null;
      }
      if (data.needs_2fa) {
        setNeeds2fa(true);
        setPending2faToken(data.pending_token || "");
        setTwoFaCode("");
        toast.message(data.message || "Enter the verification code sent to your email.");
        return null;
      }
      if (!commitSession) return data;
      const ok = await establishSession(data, { boot });
      return ok ? data : null;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const d = typeof detail === "string" ? detail : "";
      if (err.response?.status === 403 && d === "DENIED") {
        setAccessDenied(true);
      } else {
        toast.error(d || "Could not sign in");
      }
      return null;
    } finally {
      setPwBusy(false);
    }
  };

  /**
   * Attach Bearer first, confirm /auth/me, then set React user once.
   * Avoids flashing /admin then hard-redirecting on a cookie race.
   */
  const establishSession = async (data, { boot = true } = {}) => {
    if (!data?.token || !data?.user) return false;
    setSessionToken(data.token);
    try {
      // Probe without going through fetchMe's setUser — keep UI on login until confirmed.
      await api.get("/auth/me");
    } catch {
      clearSessionToken();
      toast.error("Could not establish your session. Please try signing in again.");
      return false;
    }
    login(data.token, data.user, data.client || null, { boot });
    return true;
  };

  const commitSession = async (data, { boot = true } = {}) => {
    await establishSession(data, { boot });
  };

  const complete2fa = async ({ commitSession: commit = true, boot = true } = {}) => {
    if (!pending2faToken || !twoFaCode.trim()) {
      toast.error("Enter the verification code");
      return null;
    }
    setTwoFaBusy(true);
    setAccessDenied(false);
    try {
      const { data } = await api.post("/auth/2fa/challenge", {
        pending_token: pending2faToken,
        code: twoFaCode.trim(),
      });
      setNeeds2fa(false);
      setPending2faToken("");
      setTwoFaCode("");
      if (!commit) return data;
      const ok = await establishSession(data, { boot });
      return ok ? data : null;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid code");
      return null;
    } finally {
      setTwoFaBusy(false);
    }
  };

  const resetSent = () => {
    setSent(false);
    setEmail("");
    setAccessDenied(false);
    setNeeds2fa(false);
    setPending2faToken("");
    setTwoFaCode("");
  };

  const onEmailChange = (value) => {
    setEmail(value);
    setAccessDenied(false);
  };

  const onPasswordChange = (value) => {
    setPassword(value);
    if (!showsPasswordToggle(value)) setShowPassword(false);
  };

  return {
    email,
    password,
    showPassword,
    setShowPassword,
    sent,
    busy,
    googleBusy,
    pwBusy,
    accessDenied,
    needs2fa,
    twoFaCode,
    setTwoFaCode,
    twoFaBusy,
    passwordToggleVisible,
    startGoogle,
    handleSubmit,
    passwordSignIn,
    complete2fa,
    commitSession,
    resetSent,
    onEmailChange,
    onPasswordChange,
  };
}
